import {AxiosInstance} from 'axios';
import {CustomException} from '@core/modules/common/infra/error';
import {httpMessage} from '@core/modules/common/infra/message';
import { normalizeCnpj } from '@/@core/utils/cnpj.utils';

import {Paths} from './keys';
import { CustomerGateway } from '../domain/customer.gateway';
import { UpdateCustomerInterface } from '@/@presentation/modules/schedule/pages/home/receiver';
import { SearchCustomerInterface, GetRelated } from '../domain/customer.entity';

export class CustomerHttpGateway implements CustomerGateway {
    constructor(private http: AxiosInstance) {}

    async create(params: UpdateCustomerInterface): Promise<number> {
        return this.http
            .post(Paths.default, {
                ...params,
                scheduleId: params.scheduleId
            })
            .then((res) => {
                if (!res?.data) {
                    throw new CustomException(httpMessage.updateResponseEmpty('create customer'));
                }
                return res?.data
            })
            .catch((error) => {
                if (error?.status !== 409) {
                    throw new CustomException(error?.response?.data);
                }
                throw new CustomException(httpMessage.updateError('create customer'));
            });
    }

    async update(params: UpdateCustomerInterface): Promise<number> {
        
        return this.http
            .put(Paths.default, params)
            .then((res) => {
                if (!res?.data) {
                    throw new CustomException(httpMessage.updateResponseEmpty('update customer'));
                }
                return res?.data ?? 0
            })
            .catch((error) => {
                if (error?.status !== 409) {
                    throw new CustomException(error?.response?.data);
                }
                throw new CustomException(httpMessage.updateError('update customer'));
            });
    }

    /**
     * Busca cliente por CPF ou CNPJ.
     * @param cpf CPF (11 chars) ou CNPJ (14 chars) — com ou sem máscara.
     *   A pontuação de máscara é removida via `normalizeCnpj`, preservando letras
     *   para suporte ao formato alfanumérico (RF ~07/2026).
     */
    async search(cpf: string): Promise<SearchCustomerInterface> {
        if (!cpf) {
            throw new CustomException('CPF/CNPJ é obrigatório para busca de cliente');
        }
        
        return await this.http
            .get(Paths.search + '/' + normalizeCnpj(cpf))
            .then((res) => {
                if (!res?.data) {
                    throw new CustomException('Cliente não encontrado');
                }
                return res.data;
            })
            .catch((error) => {
                if (error?.status !== 409) {
                    throw new CustomException(error?.response?.data);
                }
                throw new CustomException(httpMessage.updateError('search customer'));
            });
    }

    async getRelated(params: GetRelated): Promise<any[]> {
        return await this.http
            .post(Paths.relatedInvoices, {
                ...params,
            })
            .then((res) => {
                if (!res?.data) {
                    throw new CustomException(httpMessage.updateResponseEmpty('get related invoice'));
                }
                return res.data;
            })
            .catch((error) => {
                if (error?.status !== 409) {
                    throw new CustomException(error?.response?.data);
                }
                throw new CustomException(httpMessage.updateError('get related invoice'));
            });
    }

    async searchByRne(rne: string): Promise<SearchCustomerInterface> {
        if (!rne) {
            throw new CustomException('RNE é obrigatório para busca de cliente');
        }
        
        return await this.http
            .get(Paths.search + '/rne/' + rne.replace(/\D/g, ''))
            .then((res) => {
                if (!res?.data) {
                    throw new CustomException(httpMessage.updateResponseEmpty('search customer'));
                }
                return res.data;
            })
            .catch((error) => {
                if (error?.status !== 409) {
                    throw new CustomException(error?.response?.data);
                }
                throw new CustomException(httpMessage.updateError('search customer'));
            });
    }

    async searchById(id: number): Promise<SearchCustomerInterface> {
        if (!id || id <= 0) {
            throw new CustomException('ID válido é obrigatório para busca de cliente');
        }
        
        return await this.http
            .get(Paths.search + '/id/' + id)
            .then((res) => {
                if (!res?.data) {
                    throw new CustomException(httpMessage.updateResponseEmpty('search customer'));
                }
                return res.data;
            })
            .catch((error) => {
                if (error?.status !== 409) {
                    throw new CustomException(error?.response?.data);
                }
                throw new CustomException(httpMessage.updateError('search customer'));
            });
    }
}
